import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { Mail } from 'lucide-react';

const TAGLINES = {
  en: 'The Vertical AI Expert in Contract Risk Scanning & Management.',
  'zh-CN': '合同风险扫描与管理的垂直 AI 专家。',
  'zh-TW': '合約風險掃描與管理的垂直 AI 專家。',
};

const TRUST_NOTE = {
  en: ['LC Shield does not retain uploaded documents after analysis; see the ', 'LC Shield Privacy Policy', '. Vowpace stores redacted contract text for the life of your account; see the ', 'Vowpace Privacy Policy', '.'],
  'zh-CN': ['LC Shield 不会在分析后保留上传文件；详见 ', 'LC Shield 隐私政策', '。Vowpace 会在您的账户存续期间存储经脱敏处理的合同文本；详见 ', 'Vowpace 隐私政策', '。'],
  'zh-TW': ['LC Shield 不會在分析後保留上傳文件；詳見 ', 'LC Shield 私隱政策', '。Vowpace 會在您的帳戶存續期間儲存經遮蔽處理的合約文字；詳見 ', 'Vowpace 私隱政策', '。'],
};

const ICC_NOTE = {
  en: 'LC Shield is an independent tool and is not affiliated with or endorsed by the ICC.',
  'zh-CN': 'LC Shield 为独立工具，与国际商会（ICC）无隶属关系，亦未获其认可。',
  'zh-TW': 'LC Shield 為獨立工具，與國際商會（ICC）無隸屬關係，亦未獲其認可。',
};

const FOOTER_LINKS = [
  { to: '/', testId: 'footer-link-home', labelKey: 'nav.home' },
  { to: '/about', testId: 'footer-link-about', labelKey: 'nav.about' },
  { to: '/products', testId: 'footer-link-products', labelKey: 'nav.products' },
  { to: '/insights', testId: 'footer-link-blog', labelKey: 'nav.blog' },
  { to: '/contact', testId: 'footer-link-contact', labelKey: 'nav.contact' },
];

function FooterBrand() {
  const { t, lang } = useLanguage();
  return (
    <div className="md:col-span-4">
      <Link to="/" className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        DZent
      </Link>
      <p data-testid="footer-tagline" className="mt-4 text-sm text-white/50 leading-relaxed max-w-xs" style={{ fontFamily: "'Manrope', sans-serif" }}>
        {TAGLINES[lang] || TAGLINES['zh-TW']}
      </p>
      <div className="mt-6 space-y-1">
        <p data-testid="footer-company-name" className="text-sm font-semibold text-white/70" style={{ fontFamily: "'Manrope', 'Noto Sans TC', sans-serif" }}>
          {t('footer.company_name')}
        </p>
        <p data-testid="footer-company-registered" className="text-xs text-white/40" style={{ fontFamily: "'Manrope', 'Noto Sans TC', sans-serif" }}>
          {t('footer.registered')}
        </p>
      </div>
    </div>
  );
}

function FooterQuickLinks() {
  const { t, lang } = useLanguage();
  return (
    <div className="md:col-span-3">
      <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-white/40 mb-4">
        {lang === 'en' ? 'Quick Links' : '快速連結'}
      </h4>
      <div className="space-y-3">
        {FOOTER_LINKS.map((link) => (
          <Link
            key={link.to}
            to={link.to}
            data-testid={link.testId}
            className="block text-sm text-white/60 hover:text-[#6366F1] transition-colors"
          >
            {t(link.labelKey)}
          </Link>
        ))}
      </div>
    </div>
  );
}

function FooterContact() {
  const { t } = useLanguage();
  return (
    <div className="md:col-span-3">
      <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-white/40 mb-4">
        {t('contact.email_label')}
      </h4>
      <a
        href="mailto:info@dzentlimited.com"
        data-testid="footer-email"
        className="flex items-center gap-2 text-sm text-white/60 hover:text-[#6366F1] transition-colors"
      >
        <Mail className="h-4 w-4" />
        {t('footer.email')}
      </a>

      <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-white/40 mt-6 mb-3">
        {t('footer.legal_label')}
      </h4>
      <div className="space-y-2">
        <Link to="/privacy" data-testid="footer-link-privacy" className="block text-sm text-white/60 hover:text-[#6366F1] transition-colors">
          {t('footer.privacy')}
        </Link>
        <Link to="/terms" data-testid="footer-link-terms" className="block text-sm text-white/60 hover:text-[#6366F1] transition-colors">
          {t('footer.terms')}
        </Link>
      </div>
    </div>
  );
}

function FooterLanguage() {
  const { t, lang, setLang, SUPPORTED_LANGS } = useLanguage();
  return (
    <div className="md:col-span-2">
      <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-white/40 mb-4">
        {lang === 'en' ? 'Language' : '語言'}
      </h4>
      <div className="flex flex-wrap gap-2">
        {SUPPORTED_LANGS.map((l) => (
          <button
            key={l}
            data-testid={`footer-lang-${l}`}
            onClick={() => setLang(l)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              lang === l
                ? 'bg-[#6366F1] text-white'
                : 'bg-white/5 text-white/40 hover:bg-white/10 hover:text-white/70'
            }`}
          >
            {t(`lang.${l}`)}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Footer() {
  const { t, lang } = useLanguage();

  return (
    <footer data-testid="site-footer" className="bg-[#080B10] border-t border-slate-800/60 text-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          <FooterBrand />
          <FooterQuickLinks />
          <FooterContact />
          <FooterLanguage />
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 text-center space-y-2">
          <p data-testid="footer-trust-note" className="inline-flex flex-wrap items-center justify-center gap-x-1 gap-y-0.5 text-xs text-[#10B981]/80 max-w-3xl mx-auto">
            <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] inline-block mr-1" />
            <span>
              {(TRUST_NOTE[lang] || TRUST_NOTE['zh-TW'])[0]}
              <a href="https://getlcshield.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#10B981]">{(TRUST_NOTE[lang] || TRUST_NOTE['zh-TW'])[1]}</a>
              {(TRUST_NOTE[lang] || TRUST_NOTE['zh-TW'])[2]}
              <a href="https://vowpace.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#10B981]">{(TRUST_NOTE[lang] || TRUST_NOTE['zh-TW'])[3]}</a>
              {(TRUST_NOTE[lang] || TRUST_NOTE['zh-TW'])[4]}
            </span>
          </p>
          <p data-testid="footer-company-line" className="text-xs text-white/40">
            埵智有限公司 DZent Limited, Hong Kong.{' '}
            <a href="mailto:info@dzentlimited.com" className="hover:text-[#6366F1] transition-colors">info@dzentlimited.com</a>
            {' '}| LC Shield support:{' '}
            <a href="mailto:support@getlcshield.com" className="hover:text-[#6366F1] transition-colors">support@getlcshield.com</a>
          </p>
          <p data-testid="footer-copyright" className="text-xs text-white/30">{t('footer.copyright')}</p>
          <p data-testid="footer-icc-note" className="text-xs text-white/30">{ICC_NOTE[lang] || ICC_NOTE.en}</p>
          <p className="text-xs text-white/30">
            <a
              href="https://vowpace.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-link-vowpace"
              className="hover:text-[#6366F1] transition-colors"
            >
              Vowpace AI — Pre-signature Subcontract Clause-Risk Review
            </a>
            {' · '}
            <a href="mailto:support@vowpace.com" data-testid="footer-vowpace-support" className="hover:text-[#6366F1] transition-colors">support@vowpace.com</a>
            {' · '}
            <a href="https://vowpace.com/terms" target="_blank" rel="noopener noreferrer" data-testid="footer-vowpace-terms" className="hover:text-[#6366F1] transition-colors">Terms</a>
            {' · '}
            <a href="https://vowpace.com/privacy" target="_blank" rel="noopener noreferrer" data-testid="footer-vowpace-privacy" className="hover:text-[#6366F1] transition-colors">Privacy</a>
          </p>
        </div>
      </div>
    </footer>
  );
}
